import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Loader, Stack, VipsoProvider } from '@vipso/shared';
import { store, persistor, type RootState } from './store';
import { useSocket } from './hooks/useSocket';
import { AppBootstrap } from './components/AppBootstrap';
import { RequireFeature } from './components/RequireFeature';
import { RequireApprovalFlows } from './components/RequireApprovalFlows';
import { AppShellLayout } from './layouts/AppShellLayout';
import { LoginPage } from './pages/LoginPage';
import { OtpPage } from './pages/OtpPage';
import { OrgPickerPage } from './pages/OrgPickerPage';
import { HomePage } from './pages/HomePage';
import { TasksPage } from './pages/TasksPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { VendorsPage } from './pages/VendorsPage';
import { VendorDetailPage } from './pages/VendorDetailPage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductFormPage } from './pages/ProductFormPage';
import { ProcurementPage } from './pages/ProcurementPage';
import { ProcurementDetailPage } from './pages/ProcurementDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { StaffPage } from './pages/StaffPage';
import { WarehousePage } from './pages/WarehousePage';
import { StorageLocationsPage } from './pages/StorageLocationsPage';
import { TransportPage } from './pages/TransportPage';
import { LabourPage } from './pages/LabourPage';
import { MobileMorePage } from './pages/MobileMorePage';
import { InventoryTransferPage } from './pages/InventoryTransferPage';
import { RolesPage } from './pages/RolesPage';
import { ApprovalFlowsPage } from './pages/ApprovalFlowsPage';
import { FileViewerProvider } from './features/files/FileViewerContext';
import { OtaUpdateDialog } from './components/OtaUpdateDialog';

function ProtectedRoute() {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  useSocket(!!accessToken);

  return (
    <HashRouter>
      <AppBootstrap>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/otp" element={<OtpPage />} />
          <Route path="/org-picker" element={<OrgPickerPage />} />
          {/* Legacy bookmark — bootstrap now runs on every load */}
          <Route path="/boot" element={<Navigate to="/login" replace />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<AppShellLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<HomePage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/more" element={<MobileMorePage />} />
              <Route path="/settings" element={<SettingsPage />} />

              <Route element={<RequireFeature entitledModule="product" />}>
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:id" element={<ProductDetailPage />} />
                <Route element={<RequireFeature allOf={['product.create']} />}>
                  <Route path="/products/new" element={<ProductFormPage />} />
                </Route>
                <Route element={<RequireFeature allOf={['product.update']} />}>
                  <Route path="/products/:id/edit" element={<ProductFormPage />} />
                </Route>
              </Route>

              <Route
                element={
                  <RequireFeature
                    anyOf={['procurement.sales.view', 'procurement.purchase.view']}
                  />
                }
              >
                <Route path="/orders" element={<ProcurementPage />} />
                <Route path="/orders/:id" element={<ProcurementDetailPage />} />
                <Route path="/procurement" element={<Navigate to="/orders" replace />} />
              </Route>

              <Route element={<RequireFeature entitledModule="customer" />}>
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customers/:id" element={<CustomerDetailPage />} />
              </Route>

              <Route element={<RequireFeature entitledModule="vendor" />}>
                <Route path="/vendors" element={<VendorsPage />} />
                <Route path="/vendors/:id" element={<VendorDetailPage />} />
              </Route>

              <Route element={<RequireFeature allOf={['staff.view']} />}>
                <Route path="/staff" element={<StaffPage />} />
              </Route>

              <Route element={<RequireFeature allOf={['roles.view']} />}>
                <Route path="/roles" element={<RolesPage />} />
              </Route>

              <Route element={<RequireApprovalFlows />}>
                <Route path="/approval-flows" element={<ApprovalFlowsPage />} />
              </Route>

              <Route element={<RequireFeature allOf={['warehouse.view']} />}>
                <Route path="/warehouse" element={<WarehousePage />} />
              </Route>

              <Route element={<RequireFeature allOf={['inventory.view']} />}>
                <Route path="/inventory" element={<InventoryTransferPage />} />
              </Route>

              <Route
                element={
                  <RequireFeature anyOf={['chamber.view', 'floor.view', 'rack.view']} />
                }
              >
                <Route path="/storage" element={<StorageLocationsPage />} />
              </Route>

              <Route element={<RequireFeature allOf={['transport.view']} />}>
                <Route path="/transport" element={<TransportPage />} />
              </Route>

              <Route element={<RequireFeature allOf={['labour.view']} />}>
                <Route path="/labour" element={<LabourPage />} />
              </Route>
            </Route>
          </Route>

          <Route
            path="*"
            element={<Navigate to={accessToken ? '/dashboard' : '/login'} replace />}
          />
        </Routes>
      </AppBootstrap>
    </HashRouter>
  );
}

function ThemedApp() {
  const theme = useSelector((state: RootState) => state.auth.theme);

  return (
    <VipsoProvider
      themeOverrides={{
        theme,
        // Same as vipso-demo: theme from initApp/scope, else VIPSo default red
        primaryColor: theme?.primaryColor || '#cc202e',
        secondaryColor: theme?.secondaryColor || '#ffdbdd',
        fontFamily: theme?.fontFamily || undefined,
      }}
      colorScheme={theme?.mode === 'dark' ? 'dark' : 'light'}
    >
      <PersistGate
        loading={
          <Stack align="center" justify="center" style={{ minHeight: '100vh' }}>
            <Loader />
          </Stack>
        }
        persistor={persistor}
      >
        <FileViewerProvider>
          <AppRoutes />
          <OtaUpdateDialog />
        </FileViewerProvider>
      </PersistGate>
    </VipsoProvider>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  );
}
